import {UI} from "../../../../stem-core/ui/UIBase";
import {Money} from "../../../../stem-core/localization/Money.js";
import {apiAdminMarkChargeFailed, TopUpPayment, TopUpStatus} from "../../../../client/state/TopUpPaymentStore.js";
import {EndpointPaginator} from "../../../../stem-core/state/pagination/EndpointPaginator";
import {SimpleTable} from "../../../../dashboard/ui/SimpleTable.jsx";
import {Card} from "../../ui/Card.jsx";
import {Button} from "../../../../stem-core/ui/button/Button.jsx";
import {RevertTopUpModal} from "./RevertTopUpModal.jsx";
import {FormatPaymentMethodDescription} from "../../../../blinkpay/common/PaymentMethodDescription.jsx";
import {ConfirmationModal} from "../../../../blinkpay/ui/ConfirmationModal.jsx";
import {autoredraw} from "../../../../stem-core/decorators/AutoRedraw";
import {Link} from "../../../../stem-core/ui/primitives/Link";
import type {StoreId} from "../../../../stem-core/state/State";
import type {AdminUser} from "../../../../client/state/AdminUserStore.js";
import {RadioButtonsInput} from "../../../../core/ui/input/radio/RadioButtonsInput.jsx";
import {BlinkInputField} from "../../../../dashboard/common/Input.jsx";

async function ConfirmChargeMarkFailed(charge: TopUpPayment) {
    const confirm = await ConfirmationModal.prompt({
        title: "Confirm Failed",
        confirmLabel: "Manually set as failed",
    });

    if (confirm) {
        await apiAdminMarkChargeFailed({chargeId: charge.id});
    }
}

interface UserChargesListOptions {
    user?: AdminUser;
}

@autoredraw(TopUpPayment)
export class UserChargesList extends UI.Element<UserChargesListOptions> {
    // Lazily built in render() and reused across redraws, so no initializer to emit.
    declare paginator?: EndpointPaginator<TopUpPayment>;

    canOverwrite() {
        return false; // No reusage
    }

    render() {
        const {user} = this.options;
        let apiFilters: {userIds?: StoreId[]} = {};
        if (user) {
            apiFilters.userIds = [user.id]
        }
        this.paginator = this.paginator || new EndpointPaginator(TopUpPayment, "/admin/top_up_payments/list", apiFilters);

        const columns = [
            ["Id", (item: TopUpPayment) => item.id],
            (user == null) && ["User", (item: TopUpPayment) => <Link href={`/users/${item.user.id}`}>{item.user.getEmail()}</Link>],
            ["PP Charge Id", (item: TopUpPayment) => item.paymentProcessorChargeId],
            ["Payment Method", (item: TopUpPayment) => FormatPaymentMethodDescription(item.getPaymentMethod())],
            ["Payment processor", (item: TopUpPayment) => item.paymentMethod?.paymentProcessor],
            ["Amount", (item: TopUpPayment) => Money.format(item)],
            ["Status", (item: TopUpPayment) => item.status],
            ["Scope", (item: TopUpPayment) => item.resourceType ? `${item.resourceType} (${item.resourceId})` : item.isAutomatic ? "Auto top up" : "Manual top up"],
            ["Date", (item: TopUpPayment) => item.createdAt],
            ["Source", (item: TopUpPayment) => item.source],
            ["Action", (item: TopUpPayment) => {
                return [
                    (item.status === TopUpStatus.NOT_PROCESSED) && <Button onClick={() => ConfirmChargeMarkFailed(item)}>Mark Failed</Button>,
                    <Button onClick={() => RevertTopUpModal.show({user, charge: item})}>Revert</Button>
                ];
            }],
        ].filter(x => x); // Cleanup the falses

        return (
            <Card title="Charges">
                <BlinkInputField label="Status">
                    <RadioButtonsInput
                        values={[...TopUpStatus.all(), "Any"]}
                        initialValue="Any"
                        onChange={(value) => {
                            if (value === "Any") {
                                value = null;
                            }
                            this.paginator.updateFilter({status: value});
                        }}
                        ref="subscriptionTypeFilter"
                    />
                </BlinkInputField>
                <SimpleTable columns={columns} paginator={this.paginator}/>
            </Card>
        );

    }
}
